import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import CareerCard from '../components/CareerCard';
import SEO from '../components/SEO';
import { getAllCareers, getCategories, searchCareers } from '../services/careerService';

const CareerListingPage = () => {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [allCareers, setAllCareers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [sortMethod, setSortMethod] = useState<'newest' | 'az'>('newest');
  
  const categories = getCategories();

  useEffect(() => {
    setIsLoading(true);
    getAllCareers().then(data => {
      // Normalize data: ensure images exist for the listing
      const normalized = data.map(career => {
        if (!career.images) {
          return {
            ...career,
            images: {
              card: `/images/careers/${career.slug}/card.webp`,
              thumb: `/images/careers/${career.slug}/thumb.webp`,
            }
          };
        }
        return career;
      });
      setAllCareers(normalized);
      setIsLoading(false);
    });
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    const newParams = new URLSearchParams(searchParams);
    if (term) newParams.set('q', term);
    else newParams.delete('q');
    setSearchParams(newParams);
  };

  const handleCategoryChange = (newCategoryId: string | null) => {
     const path = newCategoryId ? `/categories/${newCategoryId}` : '/careers';
     const qs = searchTerm ? `?q=${searchTerm}` : '';
     navigate(`${path}${qs}`);
  };

  const filteredCareers = useMemo(() => {
    return searchCareers(allCareers, searchTerm, categorySlug || null, sortMethod);
  }, [allCareers, searchTerm, categorySlug, sortMethod]);

  const matchedCategory = categorySlug ? categories.find(c => c.id === categorySlug) : null;
  const currentCategoryLabel = matchedCategory ? matchedCategory.label : (categorySlug ? 'Category Not Found' : 'All Careers');

  return (
    <div className="min-h-screen bg-brand-bg pb-20">
      <SEO title={currentCategoryLabel} description={`Explore ${currentCategoryLabel.toLowerCase()} and discover your next career path.`} />
      
      <div className="bg-brand-dark border-b border-brand-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-brand-white mb-4">{currentCategoryLabel}</h1>
          <p className="text-brand-grey2 max-w-2xl">Explore our library of careers. Filter by category or search to find your perfect match.</p>
        </div>
      </div>

      <div className="sticky top-16 z-30 bg-brand-bg/95 backdrop-blur border-b border-brand-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="relative w-full lg:max-w-md group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-grey2 group-focus-within:text-brand-cyan" />
              <input
                type="text"
                className="block w-full pl-10 pr-10 py-2.5 border border-brand-border rounded-lg bg-brand-light text-brand-white focus:outline-none focus:border-brand-cyan"
                placeholder="Search careers or keywords..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <div className="flex flex-row w-full lg:w-auto gap-3 overflow-x-auto pb-1 lg:pb-0 scrollbar-hide">
              <button onClick={() => handleCategoryChange(null)} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap border ${!categorySlug ? 'bg-brand-cyan text-brand-bg border-brand-cyan' : 'bg-brand-light text-brand-grey1 border-brand-border'}`}>All</button>
              {categories.map(cat => (
                <button key={cat.id} onClick={() => handleCategoryChange(cat.id)} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap border ${categorySlug === cat.id ? 'bg-brand-cyan text-brand-bg border-brand-cyan' : 'bg-brand-light text-brand-grey1 border-brand-border'}`}>{cat.label}</button>
              ))}
              <select value={sortMethod} onChange={(e) => setSortMethod(e.target.value as 'az' | 'newest')} className="bg-brand-light text-brand-white border border-brand-border rounded-lg px-3 py-2 text-sm">
                <option value="newest">Newest</option>
                <option value="az">A-Z</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-brand-cyan animate-spin" /></div>
        ) : filteredCareers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCareers.map((career) => (
              <CareerCard key={career.slug} career={career} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 border border-dashed border-brand-border rounded-xl bg-brand-light/20">
            <h3 className="text-xl font-heading text-brand-white">No careers found</h3>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareerListingPage;
